import { useCallback, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import type { JournalCardPayload } from '@domain/journal';
import { computeHandLayout } from '@domain/journalHandLayout';
import { Sparkles } from 'lucide-react';
import { Heading } from '@/components/ui/heading';
import { JOURNAL_STEP_ICONS } from './stepIcons';
import { FlowActionAvance } from '@emovia/journal-flow';
import SelectedCard from './SelectedCard';

export type CardDeckStepProps = {
  hand: JournalCardPayload[];
  selectedCards: JournalCardPayload[];
  maxSelectable: number;
  isProcessing: boolean;
  isFinishing: boolean;
  isShuffling: boolean;
  onToggle: (cardId: number) => void;
  onShuffle: () => Promise<void>;
  onAdvance: FlowActionAvance;
};

export default function CardDeckStep({
  hand,
  selectedCards,
  maxSelectable,
  isProcessing,
  isFinishing,
  isShuffling,
  onToggle,
  onShuffle,
  onAdvance,
}: CardDeckStepProps) {
  const StepIcon = JOURNAL_STEP_ICONS.cards;
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [longHover, setLongHover] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  const setHoverWithDelay = useCallback(
    (index: number | null) => {
      setHoveredId((current) => (current == index ? null : index));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setLongHover(true);
      }, 1000);

      setLongHover(false);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setLongHover(false);
      };
    },
    [setHoveredId, setLongHover],
  );

  const unsetHoverWithDelay = useCallback(() => {
    setHoveredId(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [setHoveredId]);

  const selectedCount = selectedCards.length;
  const canSelectMore = selectedCount < maxSelectable;
  const canShuffle = hand.length > 0 && !isProcessing && !isFinishing && !isShuffling;

  const handleShuffleClick = useCallback(async () => {
    if (!canShuffle) {
      return;
    }
    await onShuffle();
  }, [canShuffle, onShuffle]);

  const renderSelectedSlots = () => (
    <div className="mb-8 flex flex-wrap justify-center gap-4">
      {Array.from({ length: maxSelectable }).map((_, index) => {
        const card = selectedCards[index];
        return (
          <div key={`selected-slot-${index}`} className="relative flex h-64 w-44">
            {card ? (
              <SelectedCard
                key={`selected-card-${card.id}`}
                text={card.text ?? 'Card'}
                category={card.category}
                onClick={() => onToggle(card.id)}
              />
            ) : (
              <div className="flex-1 flex items-center rounded-3xl justify-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70 border border-dashed border-primary/30 bg-primary/5">
                Slot libero
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderHand = () => {
    if (hand.length === 0) {
      return (
        <div className="py-12 text-center text-muted-foreground">
          <Sparkles className="mx-auto mb-3 h-10 w-10 text-primary" />
          <p className="mb-4 text-sm">Non ci sono carte da mostrare oggi.</p>
          <Button onClick={async () => await onAdvance()} disabled={isProcessing || isFinishing}>
            Vai alla scrittura
          </Button>
        </div>
      );
    }

    const total = hand.length;
    const selected = hand.filter((card) => card.selected).length;
    const { fallback, layout } = computeHandLayout(total - selected, {
      radius: 400,
      angle: 60,
    });

    let index = -1;

    return (
      <div className="relative mx-auto flex h-72 max-w-3xl items-end justify-center">
        {hand.map((card) => {
          const isSelected = card.selected;
          if (isSelected) {
            return null;
          }

          index += 1;
          const { angle, translateX, translateY } = layout[index] ?? fallback;
          const extraLift = hoveredId === card.id ? -80 : 0;
          const scale = isSelected || hoveredId === card.id ? 1.08 : 1;
          const disableBecauseLimit = !isSelected && !canSelectMore;
          const disabled = (disableBecauseLimit || isProcessing || isFinishing) && !isSelected;
          const zIndex = hoveredId === card.id && longHover ? 200 : 80 + (total - index);

          return (
            <SelectedCard
              key={`card-${card.id}`}
              className="absolute"
              style={{
                transform: `translateX(${translateX}px) translateY(${translateY}px) rotate(${angle}deg) translateY(${extraLift}px) scale(${scale})`,
                zIndex,
              }}
              onMouseEnter={() => setHoverWithDelay(card.id)}
              onMouseLeave={() => unsetHoverWithDelay()}
              onClick={() => !disabled && onToggle(card.id)}
              disabled={disabled}
              category={card.category}
              text={card.text}
              selected={isSelected}
            />
          );
        })}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <Heading
          level={2}
          icon={StepIcon}
          description={`Hai una mano di carte pescate per te. Selezionane al massimo ${maxSelectable} che senti davvero rilevanti.`}
          wrapperClassName="mb-4"
        >
          Carte del giorno
        </Heading>
      </CardHeader>

      <CardContent>
        {renderSelectedSlots()}
        {renderHand()}

        <div className="mt-8 flex flex-col items-center gap-3">
          <p className="text-sm text-muted-foreground">
            Carte selezionate: {selectedCount}/{maxSelectable}
          </p>
          {!canSelectMore && hand.length > 0 && (
            <p className="text-xs text-primary">
              Hai raggiunto il limite massimo: passa alla scrittura o deseleziona una carta.
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={handleShuffleClick} disabled={!canShuffle}>
              {isShuffling ? 'Mescolo le carte…' : 'Mescola le carte'}
            </Button>
            <Button onClick={async () => await onAdvance()} disabled={isProcessing || isFinishing}>
              {isFinishing ? 'Sto aggiornando le carte…' : 'Passa alla scrittura'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
